/**
 * The active Sub Board is work still in play. Cancelled requests stay in
 * Firestore as history, but they must leave every live board surface as soon
 * as the cancellation snapshot arrives.
 *
 * Keep this module import-free so the visibility contract can be tested
 * directly without React or Firebase.
 */
export function visibleSubRequests<T extends { status: string }>(requests: readonly T[]): T[] {
  return requests.filter((request) => request.status === 'open' || request.status === 'claimed');
}

/**
 * A row a Director wrote down by hand (packet JS1) never belongs on a LIVE
 * board - anything built on `date >= today`. TODAY IS ALLOWED for recording
 * (Bobbie records the same evening), so a same-day recorded row would
 * otherwise render on tonight's board as "Asked by <Director>", which it is
 * not: it was born covered, nobody is waiting on it, and the requester it
 * would name is whoever wrote it down rather than a real ask. It still
 * belongs in every season, week and payroll range query, which is where the
 * credit is actually read - so this filter is for the upcoming board and the
 * surfaces fed by it ONLY, never for `useWeekSubRequests` or
 * `useSeasonSubRequests`.
 *
 * Kept import-free like `visibleSubRequests` above, so every live-board
 * consumer (the board query itself, the claimed-cover lookups, the coverage
 * note) can drop a recorded row with one shared definition instead of four
 * hand-written ones drifting apart.
 */
export function liveBoardSubRequests<T extends { recordedCover?: unknown }>(requests: readonly T[]): T[] {
  return requests.filter((request) => !request.recordedCover);
}
